import { KeyRound, Trash2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'

import {
  useWebauthnCredentials,
  useWebauthnDeleteCredential,
  useWebauthnRegisterOptions,
  useWebauthnRegisterVerify,
} from '../api/auth'
import { ApiError } from '../api/client'
import { formatDateTime } from '../lib/format'
import { useWithStepUp } from '../lib/useWithStepUp'
import { createWebauthnCredential, isWebauthnSupported } from '../lib/webauthn'
import { useConfirm } from './ConfirmDialog'
import { Modal } from './Modal'
import { StepUpDialog } from './StepUpDialog'
import { useToast } from './Toast'

const DEVICE_TYPE_LABEL: Record<string, string> = {
  single_device: 'atada a este dispositivo',
  multi_device: 'sincronizada entre dispositivos',
}

/** Contenido sin el `<Modal>` propio — así `ProfileDialog` lo puede meter
 *  en una de sus pestañas sin anidar dos modales. `PasskeysDialog` de acá
 *  abajo es el mismo contenido para cuando se usa suelto. */
export function PasskeysForm({ onClose }: { onClose: () => void }) {
  const credentials = useWebauthnCredentials()
  const registerOptions = useWebauthnRegisterOptions()
  const registerVerify = useWebauthnRegisterVerify()
  const deleteCredential = useWebauthnDeleteCredential()
  const stepUp = useWithStepUp()
  const confirm = useConfirm()
  const toast = useToast()

  const [adding, setAdding] = useState(false)
  const [nickname, setNickname] = useState('')
  const [registerError, setRegisterError] = useState<string | null>(null)

  const notAvailableHere =
    credentials.isError &&
    credentials.error instanceof ApiError &&
    credentials.error.status === 404

  async function handleRegister(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setRegisterError(null)
    // El try va afuera de `stepUp.run`: si estuviera adentro, se tragaría el 403
    // de `registerOptions` y el hook nunca abriría el diálogo de contraseña.
    try {
      await stepUp.run(async () => {
        const { options } = await registerOptions.mutateAsync()
        const credential = await createWebauthnCredential(options)
        await registerVerify.mutateAsync({ credential, nickname })
        setAdding(false)
        setNickname('')
        toast.success('Llave agregada')
      })
    } catch (error) {
      setRegisterError(error instanceof Error ? error.message : 'No se pudo registrar la llave')
    }
  }

  async function handleDelete(id: number, label: string) {
    const ok = await confirm({
      title: 'Borrar llave',
      description: `Se va a borrar "${label}". Si es la única forma de segundo factor que tenés activada, vas a necesitar TOTP o un código de recuperación para entrar.`,
      confirmLabel: 'Borrar',
      tone: 'danger',
    })
    if (!ok) return
    await stepUp.run(async () => {
      await deleteCredential.mutateAsync(id)
    })
  }

  return (
    <>
      <h3>Llaves de acceso</h3>

        {notAvailableHere ? (
          <p className="muted">No disponible en este entorno.</p>
        ) : !isWebauthnSupported() ? (
          <p className="muted">Este navegador no soporta llaves de acceso.</p>
        ) : (
          <>
            <p className="muted">
              Face ID, Touch ID, Windows Hello o una llave de seguridad, como segundo factor
              alternativo al código de la app autenticadora.
            </p>

            {credentials.data && credentials.data.length > 0 && (
              <ul className="passkey-list">
                {credentials.data.map((credential) => (
                  <li key={credential.id} className="passkey-list__item">
                    <KeyRound size={16} />
                    <div className="passkey-list__info">
                      <span>{credential.nickname}</span>
                      <span className="muted">
                        {DEVICE_TYPE_LABEL[credential.device_type] ?? credential.device_type} ·
                        agregada el {formatDateTime(credential.created_at)}
                        {credential.last_used_at &&
                          ` · usada por última vez el ${formatDateTime(credential.last_used_at)}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="button button--ghost button--small"
                      aria-label={`Borrar ${credential.nickname}`}
                      onClick={() => handleDelete(credential.id, credential.nickname)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {adding ? (
              <form onSubmit={handleRegister}>
                {registerError && (
                  <div className="alert" role="alert">
                    {registerError}
                  </div>
                )}
                <div className="field">
                  <label htmlFor="passkey-nickname">Nombre para reconocerla</label>
                  <input
                    id="passkey-nickname"
                    type="text"
                    autoFocus
                    required
                    placeholder="MacBook, YubiKey…"
                    value={nickname}
                    onChange={(event) => setNickname(event.target.value)}
                  />
                </div>
                <div className="confirm-dialog__actions">
                  <button
                    className="button button--ghost"
                    type="button"
                    onClick={() => setAdding(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="button"
                    type="submit"
                    disabled={registerOptions.isPending || registerVerify.isPending}
                  >
                    {registerOptions.isPending || registerVerify.isPending
                      ? 'Registrando…'
                      : 'Continuar'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="confirm-dialog__actions">
                <button className="button button--ghost" type="button" onClick={onClose}>
                  Cerrar
                </button>
                <button className="button" type="button" onClick={() => setAdding(true)}>
                  Agregar llave
                </button>
              </div>
            )}
          </>
        )}

        {(notAvailableHere || !isWebauthnSupported()) && (
          <div className="confirm-dialog__actions">
            <button className="button button--ghost" type="button" onClick={onClose}>
              Cerrar
            </button>
          </div>
        )}

      {stepUp.promptOpen && (
        <StepUpDialog
          onCancel={stepUp.cancel}
          onConfirm={stepUp.confirm}
          isPending={stepUp.isConfirming}
          error={stepUp.confirmError}
          reason="Gestionar llaves de acceso es una acción sensible — hace falta confirmar la contraseña de nuevo."
        />
      )}
    </>
  )
}

export function PasskeysDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal onClose={onClose}>
      <PasskeysForm onClose={onClose} />
    </Modal>
  )
}
